import { useEffect, useState } from "react";
import { api, formatDateTime } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Chip } from "@/components/Chip";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Zap, Play, History, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const TRIGGER_LABELS = {
  invoice_overdue: "When an invoice is overdue",
  lead_created: "When a new lead is created",
  tax_due_in_7_days: "When a tax filing is 7 days away",
  ticket_created: "When a new ticket is created",
  task_overdue: "When a task is overdue",
};

const ACTION_LABELS = {
  email_customer: "Send email to customer",
  notify_sales_team: "Notify sales team",
  notify_finance: "Notify finance",
  notify_support: "Notify support team",
  create_task: "Create follow-up task",
};

const EVENT_TRIGGERS = new Set(["lead_created", "ticket_created"]);

export default function Automation() {
  const { user } = useAuth();
  const isManagerOrAdmin = user?.role === "admin" || user?.role === "manager";

  const [rules, setRules] = useState([]);
  const [runs, setRuns] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", trigger: "invoice_overdue", action: "email_customer", enabled: true });
  const [busyId, setBusyId] = useState(null);

  const loadRules = () => api.get("/automations").then(r => setRules(r.data));
  const loadRuns = () => api.get("/automation-runs?limit=50").then(r => setRuns(r.data));
  const load = () => { loadRules(); loadRuns(); };
  useEffect(() => { load(); }, []);

  const toggle = async (r) => {
    await api.patch(`/automations/${r.id}`, { enabled: !r.enabled });
    load();
  };

  const save = async () => {
    if (!form.name) return toast.error("Name required");
    await api.post("/automations", form);
    toast.success("Automation created");
    setOpen(false);
    setForm({ name: "", trigger: "invoice_overdue", action: "email_customer", enabled: true });
    load();
  };

  const runNow = async (r) => {
    setBusyId(r.id);
    try {
      const res = await api.post(`/automations/${r.id}/run`);
      const fires = res.data?.run?.fires_count ?? 0;
      toast.success(fires > 0 ? `Ran — ${fires} new fire${fires === 1 ? "" : "s"}` : "Ran — nothing new to fire");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Run failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Automation"
        subtitle="Reminders and notifications that run every 5 minutes — never miss an overdue invoice again"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="add-automation-btn">
                <Plus className="w-4 h-4 mr-1" /> New Rule
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New automation rule</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Rule name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="automation-name-input" /></div>
                <div><Label>Trigger (when)</Label>
                  <Select value={form.trigger} onValueChange={v => setForm({...form, trigger: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(TRIGGER_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><Label>Action (then)</Label>
                  <Select value={form.action} onValueChange={v => setForm({...form, action: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(ACTION_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
              </div>
              <DialogFooter><Button onClick={save} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="save-automation-btn">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <Tabs defaultValue="rules">
        <TabsList>
          <TabsTrigger value="rules" data-testid="tab-automation-rules"><Zap className="w-4 h-4 mr-1.5" /> Rules</TabsTrigger>
          <TabsTrigger value="runs" data-testid="tab-automation-runs"><History className="w-4 h-4 mr-1.5" /> Recent Runs</TabsTrigger>
        </TabsList>

        <TabsContent value="rules" className="mt-4 space-y-4">
          <div className="text-xs bg-blue-50 text-blue-800 border border-blue-200 px-3 py-2 rounded-md dark:bg-blue-950/40 dark:text-blue-200 dark:border-blue-900">
            Scheduled rules run automatically every 5 minutes. Event rules (new lead, new ticket) fire the moment a record is created. Managers &amp; admins can trigger a manual run.
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rules.map(r => {
              const isEvent = EVENT_TRIGGERS.has(r.trigger);
              return (
                <div key={r.id} className="kpi-card" data-testid={`automation-${r.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${r.enabled ? "bg-brand-aqua-light text-brand-aqua-hover" : "bg-slate-100 text-slate-400"}`}>
                        <Zap className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="font-medium text-slate-800">{r.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          <strong>When</strong> {TRIGGER_LABELS[r.trigger] || r.trigger}
                        </div>
                        <div className="text-xs text-slate-500">
                          <strong>Then</strong> {ACTION_LABELS[r.action] || r.action}
                        </div>
                      </div>
                    </div>
                    <Switch checked={r.enabled} onCheckedChange={() => toggle(r)} data-testid={`toggle-automation-${r.id}`} />
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Chip variant={r.enabled ? "success" : "default"}>{r.enabled ? "Active" : "Paused"}</Chip>
                      <Chip variant={isEvent ? "purple" : "info"}>{isEvent ? "event-driven" : "scheduled"}</Chip>
                      {r.last_run_at && (
                        <span className="text-[11px] text-slate-500 num inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" /> last {formatDateTime(r.last_run_at)}
                        </span>
                      )}
                      {typeof r.runs_count === "number" && (
                        <span className="text-[11px] text-slate-400 num">· {r.runs_count} runs</span>
                      )}
                    </div>
                    {isManagerOrAdmin && !isEvent && r.enabled && (
                      <Button size="sm" variant="outline" disabled={busyId === r.id}
                              onClick={() => runNow(r)}
                              className="h-7 px-2 text-xs"
                              data-testid={`run-now-${r.id}`}>
                        <Play className="w-3.5 h-3.5 mr-1" /> {busyId === r.id ? "Running…" : "Run now"}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
            {rules.length === 0 && <div className="col-span-full text-center py-12 text-slate-500 text-sm">No automations configured.</div>}
          </div>
        </TabsContent>

        <TabsContent value="runs" className="mt-4 space-y-4">
          <div className="table-wrap">
            <table className="w-full text-sm">
              <thead className="table-head">
                <tr>
                  <th className="text-left px-4 py-3">When</th>
                  <th className="text-left px-4 py-3">Rule</th>
                  <th className="text-left px-4 py-3">Trigger</th>
                  <th className="text-left px-4 py-3">Source</th>
                  <th className="text-right px-4 py-3">Fires</th>
                  <th className="text-left px-4 py-3">Details</th>
                </tr>
              </thead>
              <tbody>
                {runs.map(rn => (
                  <tr key={rn.id} className="row" data-testid={`run-row-${rn.id}`}>
                    <td className="px-4 py-3 text-xs text-slate-500 num whitespace-nowrap">{formatDateTime(rn.at)}</td>
                    <td className="px-4 py-3 text-slate-800">{rn.automation_name}</td>
                    <td className="px-4 py-3"><Chip variant="info" className="font-mono">{rn.trigger}</Chip></td>
                    <td className="px-4 py-3">
                      <Chip variant={rn.triggered_by?.startsWith("manual") ? "aqua" : rn.triggered_by === "event" ? "purple" : "default"}>
                        {rn.triggered_by?.startsWith("manual") ? "manual" : rn.triggered_by}
                      </Chip>
                    </td>
                    <td className="px-4 py-3 text-right font-heading font-semibold num">{rn.fires_count}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 max-w-[360px] truncate">
                      {rn.error ? <span className="text-red-600">Error: {rn.error}</span> :
                       (rn.fires || []).slice(0, 3).map(f => f.invoice_number || f.tax_name || f.task_title || f.target).filter(Boolean).join(", ") || "—"}
                    </td>
                  </tr>
                ))}
                {runs.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-slate-500 text-sm">No runs yet — enable a rule and click &quot;Run now&quot; or wait for the next tick.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
